import React, { useState, useMemo, useEffect} from "react";
import {Button, Radio, Typography,FormControlLabel, Grid} from "@material-ui/core";
import moment from "moment";
import { Formik, Form, Field } from "formik";
import PropTypes from "prop-types";
import { OutlineTextField } from "../../FormikCustoms";
import FormModal from "../../FormModal";
import { prettyDate } from "../../../utils/date";
import Confirm from "../../Confirm";
import DateSelector from "../../Shared/DateSelector";
import { addDays } from "date-fns";
import { isEmpty } from "lodash"

function ModalData({ editedData, filter, handleClose, handleSave, open }) {
  const [feedback, setFeedback] = useState({});
  const [openContentModal, setContentModal] = useState(false);
  const [valuesPersist, setValuesPersist] = useState(null);
  const [initValues, setInitValues] = useState({});

  const _startDate = useMemo(() => editedData.start_date ? editedData.start_date : filter, [editedData.start_date, filter]);
  const _endDate = useMemo(() => editedData.end_date ? editedData.end_date : addDays(filter, 1), [editedData.end_date, filter]);

  useEffect(() => {
   if(valuesPersist) {
      return setInitValues(valuesPersist);
   }
   setInitValues({
      description: editedData.description ?? "",
      id: editedData.id ?? null,
      starts_at: editedData.starts_at ?? "",
      ends_at: editedData.ends_at ?? "",
      capacity: editedData.capacity ? editedData.capacity.toString(): "",
      delay_max: editedData.delay_max ?? "",
      start_date: _startDate,
      end_date: editedData.end_date ?? "",
      end_date_option: handleWithEditValue(editedData)
   });
  }, [editedData, valuesPersist, _startDate]);

  const handleWithEditValue = (editedData) => {
    if (editedData?.id) {
      if (editedData?.start_date === editedData?.end_date) {
        return 0
      } else if (editedData?.start_date !== editedData?.end_date && editedData?.end_date !== null) {
        return 1
      } else if (editedData?.end_date === null) {
        return 2
      }
    }
    return -1;
  }

  const handleOnClose = () => {
    handleClose();
    setFeedback({});
    setValuesPersist(null);
  };

  const handleOnFeedback = () => {
    if (feedback.success) {
      handleOnClose();
    } else {
      setFeedback({});
    }
  };

  const isTimeValid = (timeString) => {
    if(timeString.includes('_')) return false;
    const parts = timeString.split(':');
    if (parts.length < 2 || parts[0].length !== 2 || parts[1].length !== 2) {
      return false;
    } else {
      const hours = parseInt(parts[0]);
      if(hours > 23 || hours < 0) {
        return false;
      }
      const minutes = parseInt(parts[1]);
      if (minutes > 59 || minutes < 0) {
        return false;
      }
    }
    return true;
  }

  const handleValidate = (values) => {
    const errors = {};

    if (isEmpty(values)) {
      errors.error = true
    }

    
    if (!values.description) {
      errors.description = "Descrição do turno precisa ser informado.";
      errors.error = true
    }else if(values.description.length > 50){
      errors.description = "Descrição não deve exceder o máximo de 50 caracteres.";
      errors.error = true
    }else if(values.description.length < 3){
      errors.description = "Descrição deve possuir no mínimo 3 caracteres.";
      errors.error = true
    }

    if (!values.starts_at) {
      errors.starts_at = "Horário Inicial precisa ser informado.";
      errors.error = true
    } else {
      if (!isTimeValid(values.starts_at)) {
        errors.starts_at =
          "Hora e minutos com formato incorreto, formato 24 horas.";
        errors.error = true  
      }
    }

    if (!values.ends_at) {
      errors.ends_at = "Horário Final precisa ser informado.";
      errors.error = true
    } else {
      if (!isTimeValid(values.ends_at)) {
        errors.ends_at =
          "Hora e minutos com formato incorreto, formato 24 horas.";
        errors.error = true
      }
    }

    if (!values.capacity) {
      errors.capacity = "Número de cotas precisa ser informado.";
      errors.error = true
    }

    if (values.end_date_option === -1) {
      errors.end_date_option = "Selecione uma opção de data final.";
      errors.error = true
    }

    if (!values.delay_max) {
      errors.delay_max = "Tolerância precisa ser informada.";
      errors.error = true
    } else {
      if (!isTimeValid(values.delay_max)) {
        errors.delay_max =
          "Hora e minutos com formato incorreto, formato 24 horas.";
        errors.error = true  
      }
    }

    return errors;
  };

  const saveTurn = async (values, setSubmitting) => {
    const operationFeedback = await handleSave({
      ...values,
      end_date: treatEndDate(values)
    });
    if(!operationFeedback.success){
      setValuesPersist(values);
    }
    setSubmitting(false);
    setFeedback(operationFeedback);
  };

  const treatEndDate = (values) => {
    if (values.end_date_option === 2) {
      return null;
    } else if (values.end_date_option === 1) {
      return values.end_date;
    } else {
      return moment(values.start_date).format("YYYY-MM-DD");
    }
  }

  const handleWithResumeMessage = (values) => {
    const msg = {...values}
    const endDate = treatEndDate(values);
    return `
      <div style={{
        display: "flex",
        justify-content: "center",
        flex-flow: "column",
        text-align: "left"
      }}>
        <div> Nome do turno: ${msg?.description} </div>
        <div> Começo do turno: ${msg?.starts_at} </div>
        <div> Fim do turno: ${msg?.ends_at === null ? 'Em diante' : msg?.ends_at} </div>
        <div> Data Inicial: ${prettyDate(msg?.start_date)} </div>
        <div> Data Final: ${!!endDate ? prettyDate(endDate) : "Em diante."} </div>
      </div>
    `
  }

  const configRadio = () => {
    return [
      {id: 0, label: `Somente para o dia ${prettyDate(_startDate)}`, value: _startDate, datePicker: false },
      {id: 1, label: `De ${prettyDate(_startDate)} à`, value: ((!!_endDate) ? moment(_endDate).format("YYYY-MM-DD") : []), datePicker: true },
      {id: 2, label: `De ${prettyDate(_startDate)} em diante.`, value: "", datePicker: false }
    ]
  }

  return (
    <FormModal
      title={editedData?.id ? "Editar Turno" : "Adicionar Turno"}
      open={open}
      handleClose={handleOnClose}
    >
      {!Object.keys(feedback).length ? (
        <Formik
          initialValues={initValues}
          validate={(values) => handleValidate(values)}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values, { setSubmitting }) => {
            if (openContentModal) {
              saveTurn(values, setSubmitting);
              setContentModal(false);
            } else {
              setContentModal(true);
            }
          }}
        >
          {({ setFieldValue, submitForm, isSubmitting, setSubmitting, values, errors}) => {

            return (
              <Form
               data-cy="turn-data-edit"
               id="turn-data-edit"
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Field
                      component={OutlineTextField}
                      name="description"
                      type="text"
                      data-cy="turn-description"
                      id="turn-description"
                      label="Descrição do Turno"
                      value={values.description}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Field
                      component={OutlineTextField}
                      name="starts_at"
                      type="text"
                      data-cy="turn-starts-at"
                      id="turn-starts-at"
                      mask="99:99"
                      placeholder="__:__"
                      label="Horário Inicial"
                      InputLabelProps={{ shrink: true }}
                      value={values.starts_at}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Field
                      component={OutlineTextField}
                      name="ends_at"
                      type="text"
                      data-cy="turn-ends-at"
                      id="turn-ends-at"
                      mask="99:99"
                      placeholder="__:__"
                      label="Horário Final"
                      InputLabelProps={{ shrink: true }}
                      value={values.ends_at}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Field
                      component={OutlineTextField}
                      name="capacity"
                      data-cy="turn-capacity"
                      id="turn-capacity"
                      type="number"
                      label="Nº de Cotas"
                      InputLabelProps={{ shrink: true }}
                      value={!!values.id ? !!values.capacity ? values.capacity : 0 : null}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Field
                      component={OutlineTextField}
                      name="delay_max"
                      data-cy="turn-delay"
                      id="turn-delay"
                      type="text"
                      mask="99:99"
                      placeholder="__:__"
                      label="Tolerância"
                      InputLabelProps={{ shrink: true }}
                      value={values.delay_max}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div
                      style={{
                        alignItems: "flex-start",
                        display: "flex",
                        flexDirection: "column",
                        height: 168,
                        justifyContent: "center",
                      }}
                    >
                      <div>
                        {configRadio().map((item) => {

                          return (
                            (item.datePicker === false)
                            ? 
                              <FormControlLabel
                                key={item.id}
                                name="end_date_option"
                                data-cy="turn-date-option"
                                id="turn-date-option"
                                checked={item.id === values.end_date_option}
                                value={item.id}
                                control={<Radio />}
                                onChange={(event) => {
                                  setFieldValue("end_date_option", item.id);
                                }}
                                label={item.label}
                              />
                            : <div
                                key= {item.id}
                                style={{
                                  alignItems: "center",
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <FormControlLabel
                                  key={item.id}
                                  value={item.id}
                                  name="end_date_option"
                                  data-cy="turn-end-date-option"
                                  id="turn-end-date-option"
                                  onChange={(event) => {
                                    setFieldValue("end_date_option", item.id);
                                    if (!values.end_date) {
                                      setFieldValue("end_date",moment(_startDate).add(1, 'days').format("YYYY-MM-DD"));
                                    }
                                  }}
                                  checked={item.id === values.end_date_option}
                                  control={<Radio />}
                                  label={item.label}
                                />
                                <Grid
                                  style= {{
                                    width: "50%",
                                    paddingRight: "0.5rem"
                                  }}
                                >
                                  <DateSelector
                                    initialValue={_startDate}
                                    notClearable={true}
                                    data-cy="turn-end-date-option-2"
                                    id="turn-end-date-option-2"
                                    placeholder={
                                      !!values.end_date
                                      ? moment(values.end_date).format("DD/MM/YYYY") 
                                      : null
                                    }
                                    defineMinDateFromNow={1}
                                    disabled={false}
                                    onChange={(event) => {
                                      setFieldValue("end_date_option", parseInt(item.id));
                                      setFieldValue("end_date", moment(event).format("YYYY-MM-DD"));
                                    }}
                                    selected={
                                       !!values.end_date
                                       ? moment(values.end_date).add(1, 'days').format("YYYY-MM-DD")
                                       : null}
                                  />
                                </Grid>
                              </div>
                          )
                        })}
                        </div>
                      {(errors.end_date_option && values.end_date_option === -1) && <small style={{ color: '#8f090e', paddingLeft: 15}}>
                        {
                          errors.end_date_option
                        }
                        </small>
                      }
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div
                      style={{
                        display: "flex",
                        height: 56,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        data-cy="turn-save"
                        id="turn-save"
                        style={{ minWidth: "50%" }}
                        disabled={isSubmitting}
                        size="large"
                      >
                        Salvar
                      </Button>
                      <Confirm
                        isOpen={!!openContentModal}
                        title="Deseja continuar?"
                        message={handleWithResumeMessage(values)}
                        data-cy="turn-save-continue"
                        id="turn-save-continue"
                        okText="Sim"
                        cancelText="Não"
                        handleClose={(option) => {
                          if (option) {
                            submitForm();
                          } else {
                            setContentModal(false);
                            setSubmitting(false);
                          }
                        }}

                      />
                    </div>
                  </Grid>
                </Grid>
              </Form>
            )
          }}
        </Formik>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5">{feedback.message}</Typography>
            <Typography style={{fontSize: '20px'}}>{feedback.messageAux}</Typography>
          </Grid>
          <Grid item xs={12}>
            <div
              style={{
                display: "flex",
                height: 56,
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={handleOnFeedback}
                variant="contained"
                color="primary"
                style={{ minWidth: "50%" }}
                size="large"
              >
                {feedback.success ? "Ok" : "Tentar novamente"}
              </Button>
            </div>
          </Grid>
        </Grid>
      )}
    </FormModal>
  );
}

ModalData.defaultProps = {
  editedData: {},
};

ModalData.propTypes = {
  editedData: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};

export default ModalData;
